import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL;

interface UserStats {
  totalUsers: number;
  totalProfessionals: number;
  totalAssistants: number;
  totalPatients: number;
  professionalsInTrial: number;
  professionalsPaid: number;
  professionalsExpired: number;
  totalAppointments: number;
  totalRevenue: number;
  recentUsers: Array<{
    id: number;
    nombre: string;
    email: string;
    es_profesional: boolean;
    is_assistant?: boolean;
    created_at: string;
    subscription_status?: string;
  }>;
}

interface ProfessionalMetric {
  id: number;
  nombre: string;
  apellido: string;
  email: string;
  telefono: string;
  especialidad: string;
  business_name: string;
  subscription_status: string;
  // Estado calculado por el backend a partir de trial_ends_at + current_period_end.
  // Fuente de verdad para el badge; subscription_status puede estar desactualizado
  // si el profesional aún no abrió la app desde que venció.
  subscription_effective_status?: string;
  subscription_expires_at?: string | null;
  subscription_days_until_expiry?: number | null;
  subscription_is_expired?: boolean;
  trial_ends_at: string;
  is_founder: boolean;
  professional_type: string;
  created_at: string;
  turnos_mes_actual: string;
  turnos_mes_anterior: string;
  turnos_totales: string;
  whatsapp_mes_actual: string;
  whatsapp_bloqueados: string;
  emails_mes_actual: string;
  historias_clinicas: string;
  servicios_activos: string;
  sedes: string;
  asistentes_activos: string;
  /** Visitas a la pagina publica en los ultimos 30 dias. */
  visitas_30d: string;
  ultima_visita: string | null;
  pagos: {
    ultimo_pago: string;
    monto_ultimo_pago: number;
    metodo_pago: string;
    proximo_pago: string;
    total_pagado: number;
    cantidad_pagos: number;
  } | null;
}

interface WhatsappGlobal {
  total_enviados: string;
  total_bloqueados: string;
  enviados_este_mes: string;
  bloqueados_este_mes: string;
  usuarios_activos_whatsapp: string;
}

interface TopWhatsappUser {
  user_id: number;
  nombre: string;
  apellido: string;
  email: string;
  business_name: string;
  enviados: string;
  bloqueados: string;
}

interface TurnoMes {
  mes: string;
  total: string;
  completados: string;
  cancelados: string;
  confirmados: string;
  no_show: string;
}

interface AdminMetrics {
  professionals: ProfessionalMetric[];
  whatsapp: {
    global: WhatsappGlobal;
    top_usuarios: TopWhatsappUser[];
  };
  email: {
    total_enviados: string;
    total_fallidos: string;
    enviados_este_mes: string;
  };
  turnos_por_mes: TurnoMes[];
  cloudinary: {
    archivos_clinicos: number;
    profesionales_con_archivos: number;
    imagenes_portfolio: number;
  };
}

type TabType = 'overview' | 'professionals' | 'whatsapp' | 'turnos' | 'cloudinary';

const AdminPanel: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [metrics, setMetrics] = useState<AdminMetrics | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedProfessional, setExpandedProfessional] = useState<number | null>(null);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (!userData) { navigate('/login'); return; }
    const user = JSON.parse(userData);
    const ADMIN_EMAIL = 'estebangm64@gmail.com';
    if (user.email !== ADMIN_EMAIL) { navigate('/dashboard'); return; }
    loadStats();
    loadMetrics();
    loadUso();
  }, [navigate]);

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('token')}`
  });

  // Consumo del plan de recetas contratado con Innovamed.
  const [uso, setUso] = useState<any>(null);

  const loadUso = async () => {
    try {
      const res = await fetch(`${API_URL}/api/prescriptions/uso`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setUso(data);
    } catch {
      // Si no se puede calcular, la tarjeta no se muestra y el resto del
      // panel sigue funcionando.
    }
  };

  const loadStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/stats`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setStats(data.stats);
      else throw new Error(data.message);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadMetrics = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/metrics`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setMetrics(data.metrics);
    } catch (err: any) {
      console.error('Error cargando metrics:', err);
    } finally {
      setMetricsLoading(false);
    }
  };

  const fmt = (n: number) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', minimumFractionDigits: 0 }).format(n);

  const fmtDate = (d: string) => {
    if (!d) return '-';
    const parts = d.split('T')[0].split('-');
    const date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]), 12);
    return date.toLocaleDateString('es-AR', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const fmtMonth = (d: string) => {
    if (!d) return '-';
    const parts = d.split('T')[0].split('-');
    const date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, 1);
    return date.toLocaleDateString('es-AR', { year: 'numeric', month: 'long' });
  };

  const getStatusBadge = (status?: string) => {
    const colors: Record<string, string> = {
      trial: 'bg-blue-100 text-blue-700',
      active: 'bg-green-100 text-green-700',
      founder: 'bg-yellow-100 text-yellow-700',
      expired: 'bg-red-100 text-red-700',
      trial_expired: 'bg-red-100 text-red-700',
      cancelled: 'bg-gray-100 text-gray-600'
    };
    const labels: Record<string, string> = {
      trial: 'Trial',
      active: 'Activo',
      founder: 'Founder',
      expired: 'Vencido',
      trial_expired: 'Trial vencido',
      cancelled: 'Cancelado'
    };
    return (
      <span className={`px-2 py-1 text-xs rounded-full font-medium ${colors[status || ''] || 'bg-gray-100 text-gray-600'}`}>
        {labels[status || ''] || status || 'N/A'}
      </span>
    );
  };

  // Prefiere el estado efectivo calculado en backend; cae al persistido si no vino.
  const effectiveStatusOf = (p: { subscription_effective_status?: string; subscription_status?: string }) =>
    p.subscription_effective_status || p.subscription_status;

  const n = (v: string | number) => parseInt(String(v)) || 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando panel de administracion...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <h2 className="text-red-800 font-bold mb-2">Error</h2>
          <p className="text-red-600">{error}</p>
          <button onClick={() => navigate('/dashboard')} className="mt-4 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700">Volver</button>
        </div>
      </div>
    );
  }

  if (!stats) return null;

  const filteredProfessionals = metrics?.professionals.filter(p => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (p.nombre || '').toLowerCase().includes(term) ||
      (p.apellido || '').toLowerCase().includes(term) ||
      (p.email || '').toLowerCase().includes(term) ||
      (p.business_name || '').toLowerCase().includes(term)
    );
  }) || [];

  const tabs: { key: TabType; label: string; icon: string }[] = [
    { key: 'overview', label: 'Resumen', icon: '📊' },
    { key: 'professionals', label: 'Profesionales', icon: '👩‍⚕️' },
    { key: 'whatsapp', label: 'WhatsApp & Email', icon: '📱' },
    { key: 'turnos', label: 'Turnos', icon: '📅' },
    { key: 'cloudinary', label: 'Almacenamiento', icon: '☁️' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Panel de Administracion</h1>
              <p className="text-sm text-gray-500">consultAR - Metricas en tiempo real</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => { setLoading(true); setMetricsLoading(true); loadStats(); loadMetrics(); }}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm font-medium">
                Actualizar
              </button>
              <button onClick={() => navigate('/dashboard')}
                className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 text-sm">
                Volver
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mt-4 overflow-x-auto">
            {tabs.map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-2 rounded-t-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.key
                    ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-600'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}>
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">

        {/* ==================== TAB: OVERVIEW ==================== */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <KpiCard label="Total Usuarios" value={stats.totalUsers} sub={`${stats.totalProfessionals} prof. / ${stats.totalAssistants} asist. / ${stats.totalPatients} pac.`} color="blue" />
              <KpiCard label="En Trial" value={stats.professionalsInTrial} sub="Profesionales en prueba" color="cyan" />
              <KpiCard label="Suscripciones Activas" value={stats.professionalsPaid}
                sub={stats.totalProfessionals > 0 ? `${((stats.professionalsPaid / stats.totalProfessionals) * 100).toFixed(1)}% conversion` : 'Sin conversiones'}
                color="green" />
              <KpiCard label="Expirados" value={stats.professionalsExpired}
                sub={stats.totalProfessionals > 0 ? `${((stats.professionalsExpired / stats.totalProfessionals) * 100).toFixed(1)}% churn` : '0%'}
                color="red" />
            </div>

            {/* ==========================================================
                Consumo del plan de recetas contratado con Innovamed.

                Se mira antes de encender la emisión y todos los meses: el
                plan se factura por recetas EMITIDAS, y una receta emitida no
                se puede borrar, sólo anular — y la anulación sigue contando.
                Por eso el número grande incluye las anuladas.
                ========================================================== */}
            {uso && (
              <div className={`rounded-xl p-6 text-white text-left ${
                uso.alerta === 'critica' ? 'bg-gradient-to-br from-red-600 to-rose-700'
                : uso.alerta === 'atencion' ? 'bg-gradient-to-br from-amber-500 to-orange-600'
                : 'bg-gradient-to-br from-[#0FA3B1] to-[#0B7C87]'
              }`}>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-white/80 text-sm font-medium">Recetas electrónicas · este mes</p>
                    <p className="text-4xl font-bold mt-2">
                      {uso.mes.emitidas}
                      <span className="text-xl font-normal text-white/70"> / {uso.tope}</span>
                    </p>
                    <p className="text-white/80 mt-1 text-sm">
                      Quedan {uso.restantes} del plan contratado con Innovamed
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-3xl font-bold">{uso.porcentaje}%</p>
                    <p className="text-white/70 text-xs">del plan</p>
                  </div>
                </div>

                {/* Barra de consumo */}
                <div className="mt-4 h-2 bg-white/25 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-white rounded-full transition-all"
                    style={{ width: `${Math.min(100, uso.porcentaje)}%` }}
                  />
                </div>

                <div className="mt-4 pt-4 border-t border-white/25 flex flex-wrap gap-x-6 gap-y-1 text-sm text-white/85">
                  <span>
                    {uso.mes.profesionales === 1
                      ? '1 profesional emitió este mes'
                      : `${uso.mes.profesionales} profesionales emitieron este mes`}
                  </span>
                  {uso.mes.anuladas > 0 && (
                    <span title="Las anuladas siguen contando contra el plan">
                      {uso.mes.anuladas} anulada{uso.mes.anuladas === 1 ? '' : 's'} (cuentan igual)
                    </span>
                  )}
                  <span>{uso.historico} emitidas en total</span>
                </div>

                {uso.alerta && (
                  <p className="mt-3 text-sm font-semibold">
                    {uso.alerta === 'critica'
                      ? 'Estás cerca del tope. Conviene hablar con Innovamed antes de que se corte.'
                      : 'Vas por más de tres cuartos del plan.'}
                  </p>
                )}

                {uso.porProfesional.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-white/25 space-y-1.5">
                    {uso.porProfesional.slice(0, 5).map((p: any) => (
                      <div key={p.id} className="flex items-center justify-between text-sm">
                        <span className="text-white/85 truncate pr-3">
                          {p.nombre}{p.especialidad ? ` · ${p.especialidad}` : ''}
                        </span>
                        <span className="font-semibold flex-shrink-0">{p.emitidas}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Revenue + Turnos */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-gradient-to-br from-purple-600 to-indigo-700 rounded-xl p-6 text-white">
                <p className="text-purple-200 text-sm font-medium">Ingresos por Suscripciones</p>
                <p className="text-4xl font-bold mt-2">{fmt(stats.totalRevenue)}</p>
                <p className="text-purple-200 mt-2">Pagos aprobados de profesionales</p>
                <div className="mt-4 pt-4 border-t border-purple-400/30">
                  <p className="text-purple-200 text-sm">{stats.totalAppointments} turnos agendados en total</p>
                  {metrics && (
                    <p className="text-purple-200 text-xs mt-1">
                      {metrics.professionals.filter(p => p.pagos).length} profesionales con pagos registrados
                    </p>
                  )}
                </div>
              </div>

              {metrics && (
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <h3 className="font-semibold text-gray-900 mb-4">WhatsApp & Email - Este Mes</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <MetricBox label="WA Enviados" value={n(metrics.whatsapp.global.enviados_este_mes)} color="green" />
                    <MetricBox label="WA Bloqueados" value={n(metrics.whatsapp.global.bloqueados_este_mes)} color="red" />
                    <MetricBox label="Emails Enviados" value={n(metrics.email.enviados_este_mes)} color="blue" />
                    <MetricBox label="Usuarios WA activos" value={n(metrics.whatsapp.global.usuarios_activos_whatsapp)} color="purple" />
                  </div>
                  <div className="mt-4 pt-3 border-t border-gray-100 text-xs text-gray-500">
                    Historico: {n(metrics.whatsapp.global.total_enviados)} WA enviados / {n(metrics.whatsapp.global.total_bloqueados)} bloqueados |
                    {n(metrics.email.total_enviados)} emails / {n(metrics.email.total_fallidos)} fallidos
                  </div>
                </div>
              )}
            </div>

            {/* Turnos por mes */}
            {metrics && metrics.turnos_por_mes.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Turnos por Mes (ultimos 6 meses)</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 px-3 font-semibold text-gray-600">Mes</th>
                        <th className="text-right py-2 px-3 font-semibold text-gray-600">Total</th>
                        <th className="text-right py-2 px-3 font-semibold text-green-600">Completados</th>
                        <th className="text-right py-2 px-3 font-semibold text-blue-600">Confirmados</th>
                        <th className="text-right py-2 px-3 font-semibold text-red-600">Cancelados</th>
                        <th className="text-right py-2 px-3 font-semibold text-yellow-600">No Show</th>
                      </tr>
                    </thead>
                    <tbody>
                      {metrics.turnos_por_mes.map((t, i) => (
                        <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                          <td className="py-2 px-3 font-medium capitalize">{fmtMonth(t.mes)}</td>
                          <td className="py-2 px-3 text-right font-bold">{n(t.total)}</td>
                          <td className="py-2 px-3 text-right text-green-700">{n(t.completados)}</td>
                          <td className="py-2 px-3 text-right text-blue-700">{n(t.confirmados)}</td>
                          <td className="py-2 px-3 text-right text-red-600">{n(t.cancelados)}</td>
                          <td className="py-2 px-3 text-right text-yellow-600">{n(t.no_show)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Recent Users */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Usuarios Recientes</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 px-3 font-semibold text-gray-600">Nombre</th>
                      <th className="text-left py-2 px-3 font-semibold text-gray-600">Email</th>
                      <th className="text-left py-2 px-3 font-semibold text-gray-600">Tipo</th>
                      <th className="text-left py-2 px-3 font-semibold text-gray-600">Estado</th>
                      <th className="text-left py-2 px-3 font-semibold text-gray-600">Registro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recentUsers.map(user => (
                      <tr key={user.id} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className="py-2 px-3 font-medium">{user.nombre}</td>
                        <td className="py-2 px-3 text-gray-600">{user.email}</td>
                        <td className="py-2 px-3">
                          {(() => {
                            const role = user.es_profesional
                              ? { label: 'Prof.', cls: 'bg-blue-100 text-blue-700' }
                              : user.is_assistant
                              ? { label: 'Secre', cls: 'bg-purple-100 text-purple-700' }
                              : { label: 'Pac.', cls: 'bg-gray-100 text-gray-600' };
                            return (
                              <span className={`px-2 py-0.5 text-xs rounded-full ${role.cls}`}>
                                {role.label}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="py-2 px-3">{getStatusBadge(user.subscription_status)}</td>
                        <td className="py-2 px-3 text-gray-500">{fmtDate(user.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ==================== TAB: PROFESSIONALS ==================== */}
        {activeTab === 'professionals' && (
          <div className="space-y-4">
            {metricsLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-3"></div>
                <p className="text-gray-500">Cargando metricas...</p>
              </div>
            ) : metrics ? (
              <>
                {/* Search */}
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <input type="text" placeholder="Buscar por nombre, email o negocio..."
                    value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                  <p className="text-xs text-gray-400 mt-2">{filteredProfessionals.length} profesionales</p>
                </div>

                {/* Professional Cards */}
                {filteredProfessionals.map(p => (
                  <div key={p.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    {/* Header row */}
                    <div className="p-4 cursor-pointer hover:bg-gray-50 flex items-center justify-between"
                      onClick={() => setExpandedProfessional(expandedProfessional === p.id ? null : p.id)}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-gray-900">{p.nombre} {p.apellido}</span>
                          {getStatusBadge(effectiveStatusOf(p))}
                          {p.is_founder && <span className="px-2 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-700 font-medium">Founder</span>}
                        </div>
                        <p className="text-sm text-gray-500 truncate">{p.email} {p.business_name ? `| ${p.business_name}` : ''}</p>
                      </div>
                      <div className="flex items-center gap-4 text-sm ml-4 flex-shrink-0">
                        {/* Visitas va ANTES que turnos a proposito: se lee de
                            izquierda a derecha en el orden del embudo. Cero
                            visitas y cero turnos es falta de difusion; muchas
                            visitas y cero turnos es otro problema. */}
                        <div className="text-center">
                          <p className={`font-bold text-lg ${n(p.visitas_30d) === 0 ? 'text-gray-300' : 'text-teal-700'}`}>
                            {n(p.visitas_30d)}
                          </p>
                          <p className="text-xs text-gray-400">visitas/mes</p>
                        </div>
                        <div className="text-center">
                          <p className="font-bold text-lg text-blue-700">{n(p.turnos_mes_actual)}</p>
                          <p className="text-xs text-gray-400">turnos/mes</p>
                        </div>
                        <div className="text-center">
                          <p className="font-bold text-lg text-green-700">{n(p.whatsapp_mes_actual)}</p>
                          <p className="text-xs text-gray-400">WA/mes</p>
                        </div>
                        <div className="text-center">
                          <p className="font-bold text-lg text-purple-700">{n(p.emails_mes_actual)}</p>
                          <p className="text-xs text-gray-400">emails/mes</p>
                        </div>
                        <span className="text-gray-400 text-lg">{expandedProfessional === p.id ? '▲' : '▼'}</span>
                      </div>
                    </div>

                    {/* Expanded details */}
                    {expandedProfessional === p.id && (
                      <div className="border-t border-gray-200 p-4 bg-gray-50">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                          <DetailItem label="Turnos este mes" value={n(p.turnos_mes_actual)} />
                          <DetailItem label="Turnos mes anterior" value={n(p.turnos_mes_anterior)} />
                          <DetailItem label="Turnos totales" value={n(p.turnos_totales)} />
                          <DetailItem label="Especialidad" value={p.especialidad || p.professional_type || '-'} isText />
                          <DetailItem label="WhatsApp enviados (mes)" value={n(p.whatsapp_mes_actual)} />
                          <DetailItem label="WhatsApp bloqueados (mes)" value={n(p.whatsapp_bloqueados)} highlight={n(p.whatsapp_bloqueados) > 0 ? 'red' : undefined} />
                          <DetailItem label="Emails enviados (mes)" value={n(p.emails_mes_actual)} />
                          <DetailItem label="Historias clinicas" value={n(p.historias_clinicas)} />
                          <DetailItem label="Servicios activos" value={n(p.servicios_activos)} />
                          <DetailItem label="Sedes" value={n(p.sedes)} />
                          <DetailItem label="Asistentes activos" value={n(p.asistentes_activos)} />
                          <DetailItem label="Visitas a su pagina (30d)" value={n(p.visitas_30d)} />
                          <DetailItem label="Ultima visita" value={p.ultima_visita ? fmtDate(p.ultima_visita) : 'nunca'} isText />
                          <DetailItem label="Telefono" value={p.telefono || '-'} isText />
                          <DetailItem label="Registro" value={fmtDate(p.created_at)} isText />
                          {p.trial_ends_at && <DetailItem label="Trial vence" value={fmtDate(p.trial_ends_at)} isText />}
                        </div>

                        {/* Payment info */}
                        {p.pagos ? (
                          <div className="mt-3 p-3 bg-white rounded-lg border border-gray-200">
                            <h4 className="text-sm font-semibold text-gray-700 mb-2">Pagos</h4>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                              <div>
                                <p className="text-gray-400 text-xs">Ultimo pago</p>
                                <p className="font-medium">{fmtDate(p.pagos.ultimo_pago)} - {fmt(p.pagos.monto_ultimo_pago)}</p>
                              </div>
                              <div>
                                <p className="text-gray-400 text-xs">Metodo</p>
                                <p className="font-medium">{p.pagos.metodo_pago || '-'}</p>
                              </div>
                              <div>
                                <p className="text-gray-400 text-xs">Proximo pago</p>
                                <p className="font-medium">{p.pagos.proximo_pago ? fmtDate(p.pagos.proximo_pago) : '-'}</p>
                              </div>
                              <div>
                                <p className="text-gray-400 text-xs">Total pagado</p>
                                <p className="font-bold text-green-700">{fmt(p.pagos.total_pagado)}</p>
                              </div>
                              <div>
                                <p className="text-gray-400 text-xs">Cantidad de pagos</p>
                                <p className="font-medium">{p.pagos.cantidad_pagos}</p>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <p className="text-xs text-gray-400 mt-2 italic">Sin pagos registrados</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </>
            ) : (
              <p className="text-center text-gray-500 py-8">No se pudieron cargar las metricas detalladas.</p>
            )}
          </div>
        )}

        {/* ==================== TAB: WHATSAPP & EMAIL ==================== */}
        {activeTab === 'whatsapp' && metrics && (
          <div className="space-y-6">
            {/* Global Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <KpiCard label="WA Enviados (total)" value={n(metrics.whatsapp.global.total_enviados)} color="green" />
              <KpiCard label="WA Bloqueados (total)" value={n(metrics.whatsapp.global.total_bloqueados)} color="red" />
              <KpiCard label="WA Este Mes" value={n(metrics.whatsapp.global.enviados_este_mes)} color="cyan" />
              <KpiCard label="Emails Enviados" value={n(metrics.email.total_enviados)} sub={`${n(metrics.email.total_fallidos)} fallidos`} color="blue" />
              <KpiCard label="Emails Este Mes" value={n(metrics.email.enviados_este_mes)} color="purple" />
            </div>

            {/* Costo estimado */}
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-300 rounded-xl p-6">
              <h3 className="font-bold text-yellow-800 mb-2">Estimacion de costos WhatsApp Business</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-yellow-700">Total mensajes este mes</p>
                  <p className="text-2xl font-bold text-yellow-900">{n(metrics.whatsapp.global.enviados_este_mes)}</p>
                </div>
                <div>
                  <p className="text-yellow-700">Usuarios activos en WA</p>
                  <p className="text-2xl font-bold text-yellow-900">{n(metrics.whatsapp.global.usuarios_activos_whatsapp)}</p>
                </div>
                <div>
                  <p className="text-yellow-700">Bloqueados este mes (cuota excedida)</p>
                  <p className="text-2xl font-bold text-red-600">{n(metrics.whatsapp.global.bloqueados_este_mes)}</p>
                </div>
              </div>
            </div>

            {/* Top WhatsApp Users */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Top Usuarios WhatsApp (este mes)</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 px-3 font-semibold text-gray-600">#</th>
                      <th className="text-left py-2 px-3 font-semibold text-gray-600">Profesional</th>
                      <th className="text-left py-2 px-3 font-semibold text-gray-600">Negocio</th>
                      <th className="text-right py-2 px-3 font-semibold text-green-600">Enviados</th>
                      <th className="text-right py-2 px-3 font-semibold text-red-600">Bloqueados</th>
                      <th className="text-right py-2 px-3 font-semibold text-gray-600">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {metrics.whatsapp.top_usuarios.map((u, i) => (
                      <tr key={u.user_id} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className="py-2 px-3 text-gray-400">{i + 1}</td>
                        <td className="py-2 px-3">
                          <p className="font-medium">{u.nombre} {u.apellido}</p>
                          <p className="text-xs text-gray-400">{u.email}</p>
                        </td>
                        <td className="py-2 px-3 text-gray-600">{u.business_name || '-'}</td>
                        <td className="py-2 px-3 text-right font-bold text-green-700">{n(u.enviados)}</td>
                        <td className="py-2 px-3 text-right font-bold text-red-600">{n(u.bloqueados)}</td>
                        <td className="py-2 px-3 text-right font-bold">{n(u.enviados) + n(u.bloqueados)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Per-professional WA/Email table */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Detalle por Profesional (WA + Email)</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 px-3 font-semibold text-gray-600">Profesional</th>
                      <th className="text-right py-2 px-3 font-semibold text-gray-600">WA Env.</th>
                      <th className="text-right py-2 px-3 font-semibold text-gray-600">WA Bloq.</th>
                      <th className="text-right py-2 px-3 font-semibold text-gray-600">Emails</th>
                      <th className="text-right py-2 px-3 font-semibold text-gray-600">Turnos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {metrics.professionals
                      .filter(p => n(p.whatsapp_mes_actual) > 0 || n(p.emails_mes_actual) > 0)
                      .sort((a, b) => n(b.whatsapp_mes_actual) - n(a.whatsapp_mes_actual))
                      .map(p => (
                        <tr key={p.id} className="border-b border-gray-50 hover:bg-gray-50">
                          <td className="py-2 px-3 font-medium">{p.nombre} {p.apellido}</td>
                          <td className="py-2 px-3 text-right text-green-700">{n(p.whatsapp_mes_actual)}</td>
                          <td className="py-2 px-3 text-right text-red-600">{n(p.whatsapp_bloqueados)}</td>
                          <td className="py-2 px-3 text-right text-blue-700">{n(p.emails_mes_actual)}</td>
                          <td className="py-2 px-3 text-right">{n(p.turnos_mes_actual)}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ==================== TAB: TURNOS ==================== */}
        {activeTab === 'turnos' && metrics && (
          <div className="space-y-6">
            {/* Turnos por mes table */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Turnos por Mes</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 px-3 font-semibold text-gray-600">Mes</th>
                      <th className="text-right py-2 px-3 font-semibold text-gray-600">Total</th>
                      <th className="text-right py-2 px-3 font-semibold text-green-600">Completados</th>
                      <th className="text-right py-2 px-3 font-semibold text-blue-600">Confirmados</th>
                      <th className="text-right py-2 px-3 font-semibold text-red-600">Cancelados</th>
                      <th className="text-right py-2 px-3 font-semibold text-yellow-600">No Show</th>
                    </tr>
                  </thead>
                  <tbody>
                    {metrics.turnos_por_mes.map((t, i) => {
                      const total = n(t.total);
                      const completedPct = total > 0 ? ((n(t.completados) / total) * 100).toFixed(0) : '0';
                      return (
                        <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                          <td className="py-2 px-3 font-medium capitalize">{fmtMonth(t.mes)}</td>
                          <td className="py-2 px-3 text-right font-bold text-lg">{total}</td>
                          <td className="py-2 px-3 text-right text-green-700">{n(t.completados)} <span className="text-xs text-gray-400">({completedPct}%)</span></td>
                          <td className="py-2 px-3 text-right text-blue-700">{n(t.confirmados)}</td>
                          <td className="py-2 px-3 text-right text-red-600">{n(t.cancelados)}</td>
                          <td className="py-2 px-3 text-right text-yellow-600">{n(t.no_show)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Turnos per professional */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Turnos por Profesional</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 px-3 font-semibold text-gray-600">Profesional</th>
                      <th className="text-left py-2 px-3 font-semibold text-gray-600">Estado</th>
                      <th className="text-right py-2 px-3 font-semibold text-gray-600">Este Mes</th>
                      <th className="text-right py-2 px-3 font-semibold text-gray-600">Mes Anterior</th>
                      <th className="text-right py-2 px-3 font-semibold text-gray-600">Totales</th>
                      <th className="text-right py-2 px-3 font-semibold text-gray-600">Tendencia</th>
                    </tr>
                  </thead>
                  <tbody>
                    {metrics.professionals
                      .sort((a, b) => n(b.turnos_mes_actual) - n(a.turnos_mes_actual))
                      .map(p => {
                        const actual = n(p.turnos_mes_actual);
                        const anterior = n(p.turnos_mes_anterior);
                        const diff = actual - anterior;
                        return (
                          <tr key={p.id} className="border-b border-gray-50 hover:bg-gray-50">
                            <td className="py-2 px-3">
                              <p className="font-medium">{p.nombre} {p.apellido}</p>
                              <p className="text-xs text-gray-400">{p.business_name || p.especialidad || ''}</p>
                            </td>
                            <td className="py-2 px-3">{getStatusBadge(effectiveStatusOf(p))}</td>
                            <td className="py-2 px-3 text-right font-bold text-lg">{actual}</td>
                            <td className="py-2 px-3 text-right text-gray-600">{anterior}</td>
                            <td className="py-2 px-3 text-right text-gray-600">{n(p.turnos_totales)}</td>
                            <td className="py-2 px-3 text-right">
                              {diff > 0 && <span className="text-green-600 font-medium">+{diff} ↑</span>}
                              {diff < 0 && <span className="text-red-600 font-medium">{diff} ↓</span>}
                              {diff === 0 && <span className="text-gray-400">=</span>}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ==================== TAB: CLOUDINARY ==================== */}
        {activeTab === 'cloudinary' && metrics && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <KpiCard label="Archivos Clinicos" value={metrics.cloudinary.archivos_clinicos} sub="Adjuntos en historias clinicas" color="blue" />
              <KpiCard label="Profesionales con archivos" value={metrics.cloudinary.profesionales_con_archivos} color="green" />
              <KpiCard label="Imagenes Portfolio" value={metrics.cloudinary.imagenes_portfolio} sub="Fotos de trabajos/antes-despues" color="purple" />
            </div>

            {/* Per-professional usage */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Uso por Profesional</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 px-3 font-semibold text-gray-600">Profesional</th>
                      <th className="text-right py-2 px-3 font-semibold text-gray-600">Historias Clinicas</th>
                      <th className="text-right py-2 px-3 font-semibold text-gray-600">Servicios</th>
                      <th className="text-right py-2 px-3 font-semibold text-gray-600">Sedes</th>
                      <th className="text-right py-2 px-3 font-semibold text-gray-600">Asistentes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {metrics.professionals
                      .filter(p => n(p.historias_clinicas) > 0 || n(p.servicios_activos) > 0)
                      .sort((a, b) => n(b.historias_clinicas) - n(a.historias_clinicas))
                      .map(p => (
                        <tr key={p.id} className="border-b border-gray-50 hover:bg-gray-50">
                          <td className="py-2 px-3">
                            <p className="font-medium">{p.nombre} {p.apellido}</p>
                            <p className="text-xs text-gray-400">{p.email}</p>
                          </td>
                          <td className="py-2 px-3 text-right font-bold">{n(p.historias_clinicas)}</td>
                          <td className="py-2 px-3 text-right">{n(p.servicios_activos)}</td>
                          <td className="py-2 px-3 text-right">{n(p.sedes)}</td>
                          <td className="py-2 px-3 text-right">{n(p.asistentes_activos)}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ==================== Helper Components ==================== */

const KpiCard: React.FC<{ label: string; value: number | string; sub?: string; color: string }> = ({ label, value, sub, color }) => {
  const colors: Record<string, string> = {
    blue: 'from-blue-500 to-blue-600',
    green: 'from-green-500 to-green-600',
    red: 'from-red-500 to-red-600',
    purple: 'from-purple-500 to-purple-600',
    cyan: 'from-cyan-500 to-cyan-600',
    yellow: 'from-yellow-500 to-yellow-600',
  };
  return (
    <div className={`bg-gradient-to-br ${colors[color] || colors.blue} rounded-xl p-4 text-white shadow-sm`}>
      <p className="text-white/80 text-xs font-medium">{label}</p>
      <p className="text-2xl font-bold mt-1">{value}</p>
      {sub && <p className="text-white/70 text-xs mt-1">{sub}</p>}
    </div>
  );
};

const MetricBox: React.FC<{ label: string; value: number; color: string }> = ({ label, value, color }) => {
  const colors: Record<string, string> = {
    green: 'text-green-700 bg-green-50 border-green-200',
    red: 'text-red-700 bg-red-50 border-red-200',
    blue: 'text-blue-700 bg-blue-50 border-blue-200',
    purple: 'text-purple-700 bg-purple-50 border-purple-200',
  };
  return (
    <div className={`rounded-lg p-3 border ${colors[color] || colors.blue}`}>
      <p className="text-xs opacity-70">{label}</p>
      <p className="text-xl font-bold">{value}</p>
    </div>
  );
};

const DetailItem: React.FC<{ label: string; value: string | number; isText?: boolean; highlight?: string }> = ({ label, value, isText, highlight }) => (
  <div>
    <p className="text-xs text-gray-400">{label}</p>
    <p className={`font-medium ${highlight === 'red' ? 'text-red-600' : isText ? 'text-gray-700' : 'text-gray-900'}`}>
      {value}
    </p>
  </div>
);

export default AdminPanel;
